/* CACTOX-only Shopify Ajax cart. No theme-wide files or cart contents are replaced. */
(function (scope) {
  'use strict';
  const ORIGIN = 'curvila-cactox';
  const OFFERS = Object.freeze({single: {boxes: 1, paidBoxes: 1}, trio: {boxes: 3, paidBoxes: 2}, five: {boxes: 5, paidBoxes: 3}});
  const integer = (value, fallback = 0) => Number.isSafeInteger(Number(value)) ? Number(value) : fallback;
  const sum = (items, read) => items.reduce((total, item) => total + read(item), 0);
  const properties = (line) => line.properties && typeof line.properties === 'object' ? line.properties : {};
  const variantId = (line) => String(line.variant_id || line.id || '');
  const quantity = (line) => Math.max(0, integer(line.quantity));
  const originalLine = (line) => integer(line.original_line_price, integer(line.original_price, integer(line.price)) * quantity(line));
  const finalLine = (line) => integer(line.final_line_price, integer(line.final_price, integer(line.price)) * quantity(line));
  const stable = (object) => JSON.stringify(Object.keys(object || {}).sort().map((key) => [key, String(object[key])]));
  const lineIdentity = (line) => [variantId(line), stable(properties(line)), String(line.selling_plan_allocation?.selling_plan?.id || '')].join('|');

  function offerFor(id, enabled) {
    const found = OFFERS[id];
    if (!found) throw new Error('Choose an available offer.');
    return {id, boxes: found.boxes, paidBoxes: enabled ? found.paidBoxes : found.boxes, enabled: Boolean(enabled)};
  }
  function validateQuantity(variant, total) {
    if (!variant || !variant.available) return 'This option is currently unavailable.';
    if (!Number.isSafeInteger(total) || total < 1) return 'Choose a valid quantity.';
    const rule = variant.quantity_rule || {};
    const minimum = Math.max(1, integer(rule.min, 1));
    const increment = Math.max(1, integer(rule.increment, 1));
    if (total < minimum) return `This option requires at least ${minimum} boxes in your bag.`;
    if (total % increment !== 0) return `This option must be ordered in multiples of ${increment}.`;
    if (rule.max != null && total > integer(rule.max)) return `This option allows at most ${integer(rule.max)} boxes.`;
    if (variant.inventory_management && variant.inventory_policy !== 'continue' && Number.isFinite(Number(variant.inventory_quantity)) && total > Number(variant.inventory_quantity)) return 'There is not enough stock for the complete selection.';
    return '';
  }
  function existingQuantity(cart, id) {
    return sum(cart.items || [], (line) => variantId(line) === String(id) ? quantity(line) : 0);
  }
  function groupQuantity(cart, id) {
    return sum(cart.items || [], (line) => String(properties(line)._cc_group || '') === String(id) ? quantity(line) : 0);
  }
  function preservedItems(before, after, exceptGroup) {
    const tally = (cart) => {
      const map = new Map();
      (cart.items || []).forEach((line) => {
        if (exceptGroup && properties(line)._cc_group === exceptGroup) return;
        const id = lineIdentity(line); map.set(id, (map.get(id) || 0) + quantity(line));
      });
      return map;
    };
    const old = tally(before), next = tally(after);
    return [...old].every(([key, count]) => (next.get(key) || 0) >= count);
  }
  function reductionUpdates(lines, target) {
    if (!Number.isSafeInteger(target) || target < 0 || target > sum(lines, quantity)) throw new Error('Invalid bundle reduction.');
    let remaining = target;
    const updates = {};
    lines.forEach((line) => {
      if (!line.key) throw new Error('Your bag changed. Please refresh it and try again.');
      const next = Math.min(quantity(line), remaining);
      updates[line.key] = next; remaining -= next;
    });
    return updates;
  }
  function buildGroups(cart) {
    const map = new Map();
    (cart.items || []).forEach((line) => {
      const prop = properties(line);
      const owned = prop._cc_origin === ORIGIN && Boolean(prop._cc_group) && Boolean(OFFERS[prop._cc_bundle]);
      const id = owned ? `bundle:${prop._cc_group}` : `line:${line.key}`;
      if (!map.has(id)) map.set(id, {id, owned, groupId: owned ? String(prop._cc_group) : null, lines: []});
      map.get(id).lines.push(line);
    });
    return [...map.values()].map((group) => {
      const prop = properties(group.lines[0]);
      const definition = group.owned ? OFFERS[prop._cc_bundle] : {boxes: 1, paidBoxes: 1};
      group.boxes = definition.boxes;
      group.offerEnabled = group.owned && prop._cc_offer_enabled === 'true';
      group.paidBoxes = group.offerEnabled ? definition.paidBoxes : definition.boxes;
      group.quantity = sum(group.lines, quantity);
      group.packs = group.quantity / group.boxes;
      group.valid = !group.owned || (Number.isInteger(group.packs) && group.packs > 0 && new Set(group.lines.map(variantId)).size === 1);
      group.originalPrice = sum(group.lines, originalLine);
      group.finalPrice = sum(group.lines, finalLine);
      group.expectedPrice = Math.round(group.originalPrice * group.paidBoxes / group.boxes);
      group.discountMismatch = group.offerEnabled && group.paidBoxes < group.boxes && group.finalPrice > group.expectedPrice + 1;
      group.issue = group.owned && (!group.valid || group.discountMismatch);
      return group;
    });
  }
  function cartSignature(cart) {
    return JSON.stringify([cart.currency, cart.total_price, (cart.items || []).map((line) => [line.key, quantity(line), finalLine(line)])]);
  }
  function formatMoney(cents, currency, locale) {
    try { return new Intl.NumberFormat(locale || 'en', {style: 'currency', currency: currency || 'USD'}).format(integer(cents) / 100); }
    catch (_) { return `${(integer(cents) / 100).toFixed(2)} ${String(currency || '')}`.trim(); }
  }
  function newGroupId() {
    return `cc-${scope.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`}`;
  }
  const helpers = {offerFor, validateQuantity, existingQuantity, groupQuantity, preservedItems, reductionUpdates, buildGroups, cartSignature, formatMoney, lineIdentity};

  class CactoxCart {
    constructor(root) {
      this.root = root;
      this.dialog = document.querySelector('[data-cc-cart-drawer]');
      this.editor = this.dialog?.closest('[data-cc-cart-editor]');
      try {this.settings = JSON.parse(this.editor?.querySelector('[data-cc-cart-config]')?.textContent || '{}').blocks || {};} catch (_) {this.settings = {};}
      this.form = root.querySelector('[data-cc-form]');
      this.data = JSON.parse(root.querySelector('[data-cc-product-json]')?.textContent || '{}');
      this.cart = null; this.busy = false; this.uncertain = false; this.ackSignature = null; this.destroyed = false;
      this.pendingRequests = new Set(); this.events = new AbortController(); this.nativeDrawers = new Map(); this.disabledBefore = new Map(); this.headerTriggers = new Map();
      this.lastFocus = null; this.externalDirty = false;
      if (!this.dialog || !this.form) throw new Error('The product bag is not available.');
      const base = new URL(scope.Shopify?.routes?.root || '/', scope.location.origin);
      this.base = base.origin === scope.location.origin ? base : new URL('/', scope.location.origin);
      if (!this.base.pathname.endsWith('/')) this.base.pathname += '/';
      this.standardEvents = import('@shopify/events').catch(() => null);
      this.listen(); this.suppressNative(); this.closeNative();
      customElements.whenDefined('theme-drawer').then(() => {if (this.isAlive()) this.closeNative();});
      this.observer = new MutationObserver(() => this.suppressNative());
      this.observer.observe(document.body, {childList: true, subtree: true});
      this.form.dataset.ccCommerceReady = 'true';
      root.ccCart = this;
    }
    find(selector) { return this.dialog.querySelector(selector); }
    t(key, fallback, values = {}) {
      const [group, name] = key.split('.');
      let text = this.settings?.[group]?.[name];
      text = text === undefined ? fallback : String(text);
      return text.replace(/\{([a-z_]+)\}/g, (match, token) => values[token] === undefined ? match : String(values[token]));
    }
    money(cents) { return formatMoney(cents, this.cart?.currency || this.data.currency, this.data.locale || document.documentElement.lang); }
    url(path) { return new URL(path, this.base).href; }
    isAlive() { return !this.destroyed && this.root.isConnected && this.dialog.isConnected; }
    listen() {
      const options = {signal: this.events.signal};
      this.form.addEventListener('submit', (event) => {event.preventDefault(); event.stopImmediatePropagation(); this.addSelection(event.submitter);}, {...options, capture: true});
      this.root.addEventListener('click', (event) => {
        const button = event.target.closest?.('[data-cc-add]');
        if (!button || button.type === 'submit') return;
        event.preventDefault(); event.stopImmediatePropagation(); this.addSelection(button);
      }, {...options, capture: true});
      scope.addEventListener('click', (event) => {
        if (!this.isAlive() || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const trigger = event.target.closest?.('[data-testid="cart-drawer-trigger"], header-actions a.action__cart');
        if (!trigger) return;
        event.preventDefault(); event.stopImmediatePropagation(); this.open(trigger);
      }, {...options, capture: true});
      this.dialog.addEventListener('click', (event) => {
        const target = event.target.closest?.('button');
        if (target?.matches('[data-cc-cart-close]')) this.close();
        if (target?.matches('[data-cc-cart-checkout]')) this.checkout();
        if (target?.dataset.ccCartAction) this.changeGroup(target.dataset.ccCartGroup, target.dataset.ccCartAction);
        if (event.target === this.dialog) {
          const r = this.dialog.getBoundingClientRect();
          if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.close();
        }
      }, options);
      this.dialog.addEventListener('close', () => {
        document.body.removeAttribute('data-cc-cart-open');
        document.querySelectorAll('[data-testid="cart-drawer-trigger"]').forEach((el) => el.setAttribute('aria-expanded', 'false'));
        if (this.busy) this.returnFocusPending = this.lastFocus;
        else if (this.lastFocus?.isConnected) this.lastFocus.focus({preventScroll: true});
      }, options);
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        const focusable = [...this.dialog.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),[tabindex="0"]')].filter((el) => !el.closest('[hidden]') && el.getClientRects().length);
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (!first) {event.preventDefault(); this.dialog.focus();}
        else if (event.shiftKey && (document.activeElement === first || !this.dialog.contains(document.activeElement))) {event.preventDefault(); last.focus();}
        else if (!event.shiftKey && document.activeElement === last) {event.preventDefault(); first.focus();}
      }, options);
      this.find('[data-cc-cart-ack]').addEventListener('change', (event) => {
        this.ackSignature = event.target.checked && this.cart ? cartSignature(this.cart) : null;
        this.updateCheckout();
      }, options);
      document.addEventListener('shopify:cart:lines-update', (event) => {
        if (event.ccOrigin === this) return;
        Promise.resolve(event.promise).then(() => {
          if (!this.isAlive()) return;
          if (this.busy) {this.externalDirty = true; return;}
          if (this.dialog.open) this.refresh();
        }).catch(() => {});
      }, options);
      scope.addEventListener('pageshow', (event) => {
        if (!event.persisted || !this.isAlive()) return;
        this.setBusy(false); this.closeNative(); this.refresh();
      }, options);
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target === this.root || event.target.contains?.(this.root) || event.target.contains?.(this.editor)) this.destroy();
      }, options);
    }
    closeNative() {
      const native = document.querySelector('theme-drawer#cart-drawer');
      if (typeof native?.close === 'function') native.close();
      else native?.querySelector('dialog[open]')?.close();
    }
    suppressNative() {
      if (!this.isAlive()) return;
      document.querySelectorAll('[data-testid="cart-drawer-trigger"]').forEach((el) => {
        if (!this.headerTriggers.has(el)) this.headerTriggers.set(el, {controls: el.getAttribute('aria-controls'), expanded: el.getAttribute('aria-expanded')});
        el.setAttribute('aria-controls', this.dialog.id);
        el.setAttribute('aria-expanded', String(this.dialog.open));
      });
      document.querySelectorAll('cart-drawer-component[auto-open]').forEach((el) => {
        this.nativeDrawers.set(el, true); el.removeAttribute('auto-open');
      });
    }
    destroy() {
      if (this.destroyed) return;
      this.setBusy(false); this.close(); document.body.removeAttribute('data-cc-cart-open'); this.destroyed = true; this.pendingRequests.forEach(controller => controller.abort()); this.events.abort(); this.observer?.disconnect();
      this.nativeDrawers.forEach((_, el) => {if (el.isConnected) el.setAttribute('auto-open', '');});
      this.headerTriggers.forEach((attrs, el) => {
        if (!el.isConnected) return;
        for (const [key, value] of Object.entries(attrs)) {if (value == null) el.removeAttribute(`aria-${key}`); else el.setAttribute(`aria-${key}`, value);}
      });
      delete this.root.ccCart;
    }
    async request(path, body) {
      if (!this.isAlive()) throw new Error('The product section was updated.');
      const controller = new AbortController();
      this.pendingRequests.add(controller);
      const timeout = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(this.url(path), {method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', signal: controller.signal, headers: {'Accept': 'application/json', ...(body ? {'Content-Type': 'application/json'} : {})}, ...(body ? {body: JSON.stringify(body)} : {})});
        let data; try {data = await response.json();} catch (_) {throw new Error('The store returned an unreadable response. Please review your bag before trying again.');}
        if (!this.isAlive()) throw new Error('The product section was updated.');
        if (!response.ok || data.status >= 400) {
          const message = typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : 'The store could not update your bag.';
          const error = new Error(message.slice(0, 500)); error.status = response.status; throw error;
        }
        return data;
      } catch (error) {
        if (error.name === 'AbortError') throw new Error('The request took too long. Please review your bag before trying again.');
        throw error;
      } finally {clearTimeout(timeout); this.pendingRequests.delete(controller);}
    }
    async readCart() {
      const cart = await this.request('cart.js');
      if (!Array.isArray(cart.items) || !Number.isFinite(Number(cart.total_price))) throw new Error('We could not confirm your bag. Please try again.');
      return cart;
    }
    sectionData() {
      const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((el) => el.dataset.sectionId).filter(Boolean))].slice(0, 5);
      return sections.length ? {sections, sections_url: scope.location.pathname} : {};
    }
    setBusy(busy) {
      if (this.destroyed) return;
      this.busy = busy; this.root.dataset.ccBusy = String(busy);
      this.root.setAttribute('aria-busy', String(busy)); this.dialog.setAttribute('aria-busy', String(busy));
      if (busy) {
        this.root.querySelectorAll('[data-cc-add], [data-cc-bundle], [data-cc-form] select, [data-cc-form] input:not([type="hidden"])').forEach((el) => {
          if (!this.disabledBefore.has(el)) this.disabledBefore.set(el, el.disabled);
          el.disabled = true;
        });
      } else {
        this.disabledBefore.forEach((wasDisabled, el) => {if (el.isConnected) el.disabled = wasDisabled;}); this.disabledBefore.clear();
        const currentError = this.find('[data-cc-cart-error]').textContent;
        this.root.ccRefreshProductUI?.();
        if (currentError) this.showError(currentError);
      }
      this.dialog.querySelectorAll('[data-cc-cart-action]').forEach((el) => {el.disabled = busy || el.dataset.ccUnavailable === 'true';});
      this.updateCheckout();
      if (!busy && !this.dialog.open && this.returnFocusPending?.isConnected) {this.returnFocusPending.focus({preventScroll: true}); this.returnFocusPending = null;}
      if (!busy && this.dialog.open && this.focusAfterBusy) {
        const key = this.focusAfterBusy; this.focusAfterBusy = null;
        const target = [...this.dialog.querySelectorAll('[data-cc-cart-action]')].find((el) => el.dataset.ccCartGroup === key.id && el.dataset.ccCartAction === key.action);
        (target || this.find('[data-cc-cart-close]'))?.focus({preventScroll: true});
      }
    }
    updateCheckout() {
      const button = this.find('[data-cc-cart-checkout]');
      const issues = this.cart && buildGroups(this.cart).some((group) => group.issue);
      const acknowledged = this.cart && this.ackSignature === cartSignature(this.cart);
      button.disabled = this.busy || this.uncertain || !this.cart?.items?.length || Boolean(issues && !acknowledged);
    }
    showError(message) {
      if (!this.isAlive()) return;
      const error = this.find('[data-cc-cart-error]'); error.textContent = String(message || ''); error.hidden = !message;
      this.root.querySelectorAll('[data-cc-form-errors]').forEach((el) => {el.textContent = String(message || ''); el.hidden = !message;});
    }
    status(message) {if (!this.isAlive()) return; this.find('[data-cc-cart-status]').textContent = message || '';}
    async exclusive(task, message) {
      if (this.busy || !this.isAlive()) return false;
      this.setBusy(true); this.showError(''); this.status(message);
      try {await task(); return true;}
      catch (error) {if (this.isAlive()) this.showError(error.message || 'Your bag could not be updated. Please try again.'); return false;}
      finally {
        if (this.isAlive()) {this.setBusy(false); this.status('');}
        if (this.externalDirty && this.isAlive()) {this.externalDirty = false; this.refresh();}
      }
    }
    show(trigger) {
      if (!this.isAlive()) return;
      if (!this.dialog.open) {
        this.lastFocus = trigger || document.activeElement;
        document.querySelector('theme-drawer#cart-drawer')?.close?.();
        this.dialog.showModal(); document.body.setAttribute('data-cc-cart-open', '');
        document.querySelectorAll('[data-testid="cart-drawer-trigger"]').forEach((el) => el.setAttribute('aria-expanded', 'true'));
      }
    }
    open(trigger) {this.show(trigger); if (!this.busy) this.refresh();}
    close() {if (this.dialog.open) this.dialog.close();}
    refresh() {
      return this.exclusive(async () => {
        const cart = await this.readCart(); this.uncertain = false; this.acceptCart(cart); this.syncBubble(cart);
      }, this.t('messages.updating', 'Updating your bag…'));
    }
    selection() {
      const id = String(this.form.querySelector('[name="id"]')?.value || '');
      const variant = (this.data.variants || []).find((item) => String(item.id) === id);
      const selected = this.root.querySelector('[data-cc-bundle]:checked');
      const offer = offerFor(selected?.value || 'single', this.root.dataset.offersEnabled === 'true');
      if (!variant) throw new Error('Choose an available product option.');
      return {variant, offer};
    }
    formProperties() {
      const result = {};
      for (const [key, value] of new FormData(this.form)) {
        const match = key.match(/^properties\[([^\]]+)\]$/);
        if (!match || match[1].startsWith('_cc_')) continue;
        if (typeof value !== 'string') {
          if (value.size) throw new Error('File personalization cannot be added with this bundle. Please contact the store.');
          continue;
        }
        if (value) result[match[1]] = value;
      }
      return result;
    }
    async addSelection(trigger) {
      if (this.busy || !this.form.reportValidity()) return;
      if (this.uncertain) {this.open(trigger); return;}
      let selected, suppliedProperties, sellingPlan;
      try {
        selected = this.selection(); suppliedProperties = this.formProperties();
        sellingPlan = this.form.querySelector('[name="selling_plan"]')?.value || '';
        if (this.data.requiresSellingPlan && !sellingPlan) throw new Error('Choose a purchase plan before adding this product.');
      } catch (error) {this.showError(error.message); return;}
      const {variant, offer} = selected;
      const groupId = newGroupId();
      const lineProperties = {...suppliedProperties, _cc_origin: ORIGIN, _cc_group: groupId, _cc_bundle: offer.id, _cc_boxes: String(offer.boxes), _cc_paid_boxes: String(offer.paidBoxes), _cc_offer_enabled: String(offer.enabled), _cc_product: String(this.data.productId || '')};
      await this.exclusive(async () => {
        const before = await this.readCart();
        const invalid = validateQuantity(variant, existingQuantity(before, variant.id) + offer.boxes);
        if (invalid) throw new Error(invalid);
        const item = {id: integer(variant.id), quantity: offer.boxes, properties: lineProperties};
        if (sellingPlan) item.selling_plan = integer(sellingPlan);
        const {cart, sections} = await this.addAndVerify(before, item, groupId, offer.boxes);
        this.acceptCart(cart); await this.publish(cart, 'add', sections, [{merchandiseId: String(variant.id), quantity: offer.boxes}]);
        const issues = buildGroups(cart).some((group) => group.issue);
        if (!this.isAlive()) return;
        if (this.root.dataset.directCheckout === 'true' && !issues) {
          scope.location.assign(this.url('checkout'));
        } else {
          this.show(trigger);
          if (issues) this.status(this.t('messages.review_status', 'Please review your offer and the current prices before checkout.'));
        }
      }, this.t('messages.adding', 'Adding your selection…'));
    }
    async addAndVerify(before, item, groupId, expectedQuantity) {
      let response, failure;
      try {response = await this.request('cart/add.js', {items: [item], ...this.sectionData()});}
      catch (error) {failure = error;}
      let cart;
      try {cart = await this.readCart();}
      catch (_) {
        this.uncertain = true; this.show();
        throw new Error('We could not confirm whether your selection was added. Open your bag to check it before adding again.');
      }
      const actual = groupQuantity(cart, groupId);
      if (actual !== expectedQuantity) {
        const oldQuantity = groupQuantity(before, groupId);
        if (actual > oldQuantity) {
          const affected = cart.items.filter((line) => properties(line)._cc_group === groupId);
          try {
            await this.request('cart/update.js', {updates: reductionUpdates(affected, oldQuantity), ...this.sectionData()});
            cart = await this.readCart();
            if (groupQuantity(cart, groupId) !== oldQuantity) throw new Error('Incomplete rollback');
          } catch (_) {
            this.uncertain = true; this.acceptCart(cart); this.show();
            throw new Error('Only part of the selection may be in your bag. Please review or remove it before continuing.');
          }
        }
        this.acceptCart(cart); await this.publish(cart, 'update', response?.sections);
        throw new Error(failure?.message || 'The complete selection could not be added. Your previous bag has been preserved.');
      }
      if (!preservedItems(before, cart, groupId)) {
        this.acceptCart(cart); this.show();
        throw new Error('Your bag changed while the selection was added. Review all items before continuing.');
      }
      this.uncertain = false;
      return {cart, sections: response?.sections};
    }
    async changeGroup(id, action) {
      if (this.busy || !['increase', 'decrease', 'remove'].includes(action)) return;
      this.focusAfterBusy = {id, action};
      await this.exclusive(async () => {
        const before = await this.readCart(); this.acceptCart(before);
        const group = buildGroups(before).find((item) => item.id === id);
        if (!group) throw new Error('Your bag changed. The current items are shown now.');
        const first = group.lines[0];
        let response, cart, mutationAttempted = false;
        try {
        if (group.owned && action === 'increase') {
          if (!group.valid) throw new Error('Remove this incomplete bundle and choose a new offer.');
          const known = (this.data.variants || []).find((v) => String(v.id) === variantId(first));
          if (known) {
            const error = validateQuantity(known, existingQuantity(before, known.id) + group.boxes);
            if (error) throw new Error(error);
          }
          const item = {id: integer(variantId(first)), quantity: group.boxes, properties: {...properties(first)}};
          const plan = first.selling_plan_allocation?.selling_plan?.id;
          if (plan) item.selling_plan = integer(plan);
          mutationAttempted = true;
          ({cart} = await this.addAndVerify(before, item, group.groupId, group.quantity + group.boxes));
        } else if (group.owned) {
          if (action !== 'remove' && !group.valid) throw new Error('Remove this incomplete bundle and choose a new offer.');
          const target = action === 'remove' ? 0 : Math.max(0, group.quantity - group.boxes);
          const known = (this.data.variants || []).find((v) => String(v.id) === variantId(first));
          const total = existingQuantity(before, variantId(first)) - group.quantity + target;
          if (known && total > 0) {
            const error = validateQuantity({...known, available: true, inventory_management: null}, total);
            if (error) throw new Error(error);
          }
          mutationAttempted = true;
          response = await this.request('cart/update.js', {updates: reductionUpdates(group.lines, target), ...this.sectionData()});
          cart = await this.readCart();
          if (groupQuantity(cart, group.groupId) !== target) throw new Error('The full bundle change could not be confirmed. Refresh your bag before continuing.');
        } else {
          const rule = first.quantity_rule || {};
          const increment = Math.max(1, integer(rule.increment, 1));
          const min = Math.max(1, integer(rule.min, 1));
          let target = action === 'remove' ? 0 : quantity(first) + (action === 'increase' ? increment : -increment);
          if (target < min) target = 0;
          mutationAttempted = true;
          response = await this.request('cart/change.js', {id: first.key, quantity: target, ...this.sectionData()});
          cart = await this.readCart();
        }
        this.uncertain = false; this.acceptCart(cart); await this.publish(cart, action === 'remove' ? 'remove' : 'update', response?.sections);
        } catch (error) {
          if (mutationAttempted) {
            try {
              const current = await this.readCart(); this.uncertain = false; this.acceptCart(current); await this.publish(current, 'update');
            } catch (_) {
              this.uncertain = true;
              throw new Error('We could not confirm the change. Reopen your bag to refresh it before continuing.');
            }
          }
          throw error;
        }
      }, this.t('messages.updating', 'Updating your bag…'));
    }
    acceptCart(cart) {
      if (!this.isAlive()) return;
      this.cart = cart;
      if (this.ackSignature !== cartSignature(cart)) this.ackSignature = null;
      this.render();
      this.root.dispatchEvent(new CustomEvent('cc:cart-updated', {bubbles: true, detail: {cart}}));
    }
    syncBubble(cart) {
      if (!this.isAlive()) return;
      document.querySelectorAll('cart-icon').forEach((icon) => {
        if (typeof icon.renderCartBubble === 'function') {Promise.resolve(icon.renderCartBubble(cart.item_count, false)).catch(() => {}); return;}
        const count = icon.querySelector('[ref="cartBubbleCount"]'); if (count) {count.textContent = cart.item_count < 100 ? String(cart.item_count) : ''; count.classList.toggle('hidden', !cart.item_count);}
        icon.querySelector('[ref="cartBubble"]')?.classList.toggle('visually-hidden', !cart.item_count);
        icon.classList.toggle('header-actions__cart-icon--has-cart', cart.item_count > 0);
      });
    }
    async publish(cart, action, sections, lines) {
      if (!this.isAlive()) return;
      this.suppressNative(); this.syncBubble(cart);
      const module = await this.standardEvents;
      if (!this.isAlive()) return;
      const detail = {didError: false, itemCount: cart.item_count, source: 'curvila-cactox-cart', sourceId: this.root.dataset.sectionId, items: cart.items, ...(sections ? {sections} : {})};
      let event;
      try {
      if (module?.CartLinesUpdateEvent) {
        event = new module.CartLinesUpdateEvent({action, context: 'product', lines: lines || [], promise: Promise.resolve({cart: module.CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail})});
      }
      } catch (_) { /* Cart remains usable if an optional native event adapter changes. */ }
      if (!event) {
        event = new Event('shopify:cart:lines-update', {bubbles: true});
        event.action = action; event.context = 'product'; event.promise = Promise.resolve({cart: {totalQuantity: cart.item_count}, detail});
      }
      event.ccOrigin = this; this.root.dispatchEvent(event);
      this.root.dispatchEvent(new CustomEvent('cc:cart-updated', {bubbles: true, detail: {cart, action}}));
    }
    node(tag, className, text) {
      const el = document.createElement(tag); if (className) el.className = className; if (text !== undefined) el.textContent = String(text); return el;
    }
    safeUrl(value, external) {
      if (!value || typeof value !== 'string' || !value.trim()) return null;
      try {const url = new URL(value, scope.location.origin); return ['https:', 'http:'].includes(url.protocol) && (external || url.origin === scope.location.origin) ? url.href : null;} catch (_) {return null;}
    }
    actionButton(label, action, group, unavailable) {
      const el = this.node('button', '', label); el.type = 'button'; el.dataset.ccCartAction = action; el.dataset.ccCartGroup = group.id;
      el.dataset.ccUnavailable = String(Boolean(unavailable)); el.disabled = this.busy || Boolean(unavailable); return el;
    }
    renderGroup(group) {
      const first = group.lines[0];
      const card = this.node('article', `cc-cart__item${group.owned ? ' cc-cart__item--bundle' : ''}`);
      const imageUrl = this.safeUrl(first.featured_image?.url || first.image || '', true);
      if (imageUrl) {
        const image = this.node('img', 'cc-cart__image'); image.src = imageUrl; image.alt = first.featured_image?.alt || first.product_title || first.title || 'Product'; image.width = 82; image.height = 98; image.loading = 'lazy'; card.append(image);
      } else card.append(this.node('div', 'cc-cart__placeholder'));
      const body = this.node('div', 'cc-cart__item-content');
      const href = this.safeUrl(first.url || '', false);
      const title = this.node(href ? 'a' : 'span', 'cc-cart__name', first.product_title || first.title || 'Product'); if (href) title.href = href; body.append(title);
      const variants = [...new Set(group.lines.map((line) => line.variant_title).filter((title) => title && title !== 'Default Title'))];
      if (variants.length) body.append(this.node('p', 'cc-cart__variant', variants.join(' · ')));
      Object.entries(properties(first)).filter(([key, value]) => !key.startsWith('_') && value != null && String(value)).forEach(([key, value]) => body.append(this.node('p', 'cc-cart__property', `${key}: ${value}`)));
      if (first.selling_plan_allocation?.selling_plan?.name) body.append(this.node('p', 'cc-cart__plan', first.selling_plan_allocation.selling_plan.name));
      if (group.owned) {
        const offerName = {single: this.t('items.single', 'Buy 1'), trio: this.t('items.trio', 'Buy 2 + 1 FREE'), five: this.t('items.five', 'Buy 3 + 2 FREE')}[properties(first)._cc_bundle];
        const attested = group.offerEnabled && group.valid && !group.discountMismatch;
        body.append(this.node('p', 'cc-cart__offer-name', attested ? offerName : this.t('items.selection', '{boxes}-box selection', {boxes:group.boxes})));
        body.append(this.node('span', 'cc-cart__badge', this.t('items.badge', '{quantity} {unit} · {masks} masks', {quantity:group.quantity,unit:this.t(group.quantity === 1 ? 'items.box' : 'items.boxes',group.quantity === 1 ? 'box' : 'boxes'),masks:group.quantity * 4})));
        if (attested && group.boxes > 1) body.append(this.node('p', 'cc-cart__shipping', this.t('items.shipping', 'Free shipping on this bundle')));
      }
      const price = this.node('p', 'cc-cart__price'); price.append(this.node('strong', '', this.money(group.finalPrice)));
      if (group.originalPrice > group.finalPrice) price.append(this.node('s', '', this.money(group.originalPrice))); body.append(price);
      const discounts = new Map();
      group.lines.forEach((line) => (line.line_level_discount_allocations || []).forEach((discount) => {
        const name = discount.discount_application?.title || 'Discount'; discounts.set(name, (discounts.get(name) || 0) + integer(discount.amount));
      }));
      discounts.forEach((amount, name) => body.append(this.node('p', 'cc-cart__line-discount', `${name}: −${this.money(amount)}`)));
      const controls = this.node('div', 'cc-cart__controls');
      const quantityWrap = this.node('div'); const stepper = this.node('div', 'cc-cart__quantity');
      const minus = this.actionButton('−', 'decrease', group, group.owned && !group.valid); minus.setAttribute('aria-label', group.owned ? `Remove one ${group.boxes}-box selection` : 'Decrease quantity');
      const plus = this.actionButton('+', 'increase', group, group.owned && !group.valid); plus.setAttribute('aria-label', group.owned ? `Add one ${group.boxes}-box selection` : 'Increase quantity');
      stepper.append(minus, this.node('span', '', group.owned && group.valid ? group.packs : group.quantity), plus); quantityWrap.append(stepper);
      if (group.owned) quantityWrap.append(this.node('p', 'cc-cart__quantity-label', group.valid ? (group.boxes === 1 ? this.t('items.single_quantity', 'Boxes') : this.t('items.bundle_quantity', 'Bundles of {boxes} boxes', {boxes:group.boxes})) : this.t('items.incomplete', 'Incomplete bundle — remove to choose again')));
      const remove = this.actionButton(this.t('items.remove', 'Remove'), 'remove', group); remove.className = 'cc-cart__remove'; controls.append(quantityWrap, remove); body.append(controls); card.append(body); return card;
    }
    render() {
      if (!this.cart) return;
      const cart = this.cart, groups = buildGroups(cart);
      this.find('[data-cc-cart-count]').textContent = `(${integer(cart.item_count)})`;
      const container = this.find('[data-cc-cart-items]');
      const priorControl = document.activeElement?.closest?.('[data-cc-cart-action]');
      const focusKey = priorControl && this.dialog.contains(priorControl) ? {id: priorControl.dataset.ccCartGroup, action: priorControl.dataset.ccCartAction} : null;
      container.replaceChildren();
      if (!groups.length && this.settings.empty) {
        const empty = this.node('div', 'cc-cart__empty'); empty.append(this.node('h3', '', this.t('empty.title', 'Your bag is empty')), this.node('p', '', this.t('empty.body', 'Choose your favorite selection and we will keep it here for you.')));
        const back = this.node('button', 'cc-cart__checkout', this.t('empty.button', 'Continue shopping')); back.type = 'button'; back.dataset.ccCartClose = ''; empty.append(back); container.append(empty);
      } else groups.forEach((group) => container.append(this.renderGroup(group)));
      this.find('[data-cc-cart-footer]').hidden = !groups.length;
      this.find('[data-cc-cart-total]').textContent = this.money(cart.total_price);
      const trust = this.find('[data-cc-cart-trust]'); if (trust) trust.hidden = !groups.some((group) => group.owned);
      this.find('[data-cc-cart-tax-note]').textContent = cart.taxes_included ? this.t('subtotal.taxes_included', 'Taxes included. Shipping is calculated at checkout.') : this.t('subtotal.taxes_excluded', 'Shipping and taxes are calculated at checkout.');
      const discountContainer = this.find('[data-cc-cart-discounts]'); discountContainer.replaceChildren();
      (cart.cart_level_discount_applications || []).forEach((discount) => {
        const row = this.node('div', 'cc-cart__discount'); row.append(this.node('span', '', discount.title || 'Discount'), this.node('span', '', `−${this.money(discount.total_allocated_amount)}`)); discountContainer.append(row);
      });
      const issues = groups.filter((group) => group.issue);
      this.find('[data-cc-cart-offer-warning]').hidden = !issues.length;
      this.find('[data-cc-cart-offer-message]').textContent = issues.some((group) => !group.valid)
        ? this.t('messages.incomplete_warning', 'A bundle is incomplete. Remove it and choose a new selection, or review the quantities and current prices before continuing.')
        : issues.length ? this.t('messages.discount_warning', 'The advertised bundle discount has not been fully applied. The prices shown here are the actual Shopify cart prices. Review them before continuing.') : '';
      this.find('[data-cc-cart-ack]').checked = this.ackSignature === cartSignature(cart);
      this.updateCheckout();
      if (focusKey && this.busy) this.focusAfterBusy = focusKey;
      else if (focusKey) {
        const replacement = [...container.querySelectorAll('[data-cc-cart-action]')].find((el) => el.dataset.ccCartGroup === focusKey.id && el.dataset.ccCartAction === focusKey.action);
        (replacement || this.find('[data-cc-cart-close]'))?.focus({preventScroll: true});
      }
    }
    checkout() {
      if (this.busy) return;
      return this.exclusive(async () => {
        const cart = await this.readCart(); this.uncertain = false; this.acceptCart(cart);
        if (!cart.items.length) return;
        const issues = buildGroups(cart).some((group) => group.issue);
        if (issues && this.ackSignature !== cartSignature(cart)) {
          this.find('[data-cc-cart-offer-warning]').scrollIntoView({block: 'nearest'});
          this.find('[data-cc-cart-ack]').focus(); return;
        }
        if (this.isAlive()) scope.location.assign(this.url('checkout'));
      }, this.t('messages.checking', 'Checking your bag…'));
    }
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = {helpers, CactoxCart};
  if (typeof document === 'undefined') return;
  if (scope.CurvilaCactoxCart) {scope.CurvilaCactoxCart.init(); return;}
  const controllers = new Map();
  function active() {return [...controllers.values()].find((controller) => controller.isAlive());}
  function configureOpenCart() {
    scope.Shopify?.actions?.openCart?.configure?.({async handler() {
      const controller = active();
      if (controller) {controller.open(document.activeElement); return;}
      const drawer = document.querySelector('theme-drawer#cart-drawer');
      if (drawer?.open) drawer.open();
      else scope.location.assign(scope.Theme?.routes?.cart_url || `${scope.Shopify?.routes?.root || '/'}cart`);
    }});
  }
  function init() {
    controllers.forEach((controller, root) => {if (!controller.isAlive()) {controller.destroy(); controllers.delete(root);}});
    document.querySelectorAll('[data-cc-product]').forEach((root) => {
      if (controllers.has(root) || !document.querySelector('[data-cc-cart-drawer]')) return;
      try {controllers.set(root, new CactoxCart(root));}
      catch (error) {console.warn('[curvila-cactox] Cart initialization failed:', error);}
    });
    configureOpenCart();
  }
  scope.CurvilaCactoxCart = {helpers, CactoxCart, init};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => {init(); setTimeout(configureOpenCart, 0);}, {once: true});
  else {init(); setTimeout(configureOpenCart, 0);}
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', () => setTimeout(init, 0));
  document.addEventListener('shopify:section:select', (event) => {const controller = active(); if (controller?.editor && event.target.contains(controller.editor)) controller.open();});
  document.addEventListener('shopify:block:select', (event) => {const controller = active(); if (controller?.editor && controller.editor.contains(event.target)) controller.open();});
  document.addEventListener('click', (event) => {if (event.target.closest?.('[data-cc-cart-editor-open]')) active()?.open(event.target);});
})(typeof window !== 'undefined' ? window : globalThis);
